import React, {useEffect} from "react";
import {
  Tabs,
  Tab,
  Box,
  Typography, CircularProgress,
} from "@material-ui/core";
import SectionRetry from "../../SectionRetry";
import { useTranslation } from "react-i18next";


//モーダルをインポート
import {
  ConnectingFlightModal,
  FlightModalApi
} from "./../../modal"

//scss
import "../../../scss/common.scss";
import "../../../scss/component.scss";

import LoadingOverlay from 'react-loading-overlay-ts';

// lib
import { 
  getFlightCodeLabel,
  getFlightCodeByLabel, 
  getFlightTime,
  getAirLineNameInJalGroup,
  getAirLineNameInAnaGroup,
  getAirLineLogo,
  getFlightNumberForDisplay
} from "../../../utils/flight";
import dayjs from "dayjs";

// types
import { 
  Flights,
  FlightType,
  FReservationFlightEntity,
  FlightEntity 
} from "../../../types/Flight";
import { PlanSearch } from "../../../types/Search";

// config
import { airCode } from "../../../config/Airport";
import {
  currentFlightEntity,
  flightsEntity,
  FReservationConnectionFlight,
  FReservationFlight,
  FReservationFlights
} from "../../../types/FlightV2";
import {getCurrentFlight, getFlightClassLabel, isConnection, lastCurrentFlight} from "../../../utils/flightV2";
import {PlanSelectFormData} from "../../../types/planSelectForm";
import {makeStyles} from "@material-ui/core/styles";

type Props = {
  planSelectFormData: PlanSelectFormData
  search: PlanSearch | undefined,
  canSelect: boolean,
  isLoading: {
    all: boolean,
    ANA: boolean,
    JAL: boolean
  },
  flights: flightsEntity,
  currentFlightEntities: currentFlightEntity[],
  anaHasNoFlight: boolean,
  jalHasNoFlight: boolean,
  changeAirportCallback: (airport:string) => void,
  changeFlightCallback: (airline:string, action:string, currentFlights:FReservationFlight[], airport: string | null | undefined ) => void,
  profitRate: string | undefined,
  /**
   * **全社とも便を出せなかった**とき（issue #2992 / 2026-09-04 T3・条件 A）。
   * ★便が 1 社でも出ているなら立てない。以前は `errors` が 1 件でもあれば出していたため、
   *   便が並んでいるのに「一部の便を取得できませんでした」が出て紛らわしかった。
   */
  flightError?: {retryAfterSec: number},
  /** フライト区画だけを再要求する（ページ全体を読み直さない） */
  onRetryFlight?: () => void,
  /**
   * 航空会社ごとの取得失敗（2026-09-04 T3・条件 B）。
   * ★「その社は 0 便＋errors あり／もう 1 社は便あり」のとき、その社のタブ内にだけ出す。
   */
  airlineErrors?: {[airline: string]: {retryAfterSec: number}},
  /** その航空会社だけ取り直す（もう 1 社の便と選択は維持する） */
  onRetryFlightAirline?: (airline: string) => void,
  /**
   * 便の選択を止めるか（2026-09-07）。
   * ★基本プランの読み込み中など、**このセクション以外の理由**で選ばせたくないときに立てる。
   *   ホテルを選び切ると次のページへ進めてしまうため、古い内容で確定されるのを防ぐ。
   */
  selectDisabled?: boolean,
}



// flight tab loadingのスタイル
const useStyles = makeStyles((theme) => ({
  flightTabLoading: {
    marginLeft: '10px',
    verticalAlign: 'middle',
    color: '#1A3268'
  },
}));

const SectionFlight: React.FC<Props> = ({
  planSelectFormData,
  search,
  canSelect,
  isLoading,
  flights,
  currentFlightEntities,
  anaHasNoFlight,
  jalHasNoFlight,
  changeAirportCallback,
  changeFlightCallback,
  profitRate,
  flightError,
  onRetryFlight,
  airlineErrors,
  onRetryFlightAirline,
  selectDisabled,
}) => {
  const { t, i18n } = useTranslation();
  const loadingStyle = useStyles();


  // 乗継便の到着時間を取得
  const getArrivalFlight = (flights:FReservationFlight[]): FReservationConnectionFlight | undefined  => {
    if(flights && flights.length > 0) {
      const lastFlight = lastCurrentFlight(flights[0].FReservationConnectionFlight);
      if (lastFlight.length > 0) {
        return lastFlight[0];
      }
    }
    return undefined;
  }


  const airlines = (
    name: string, // ANA,JAL,etc..
    index: number,
    logo: string,
    inbound: FReservationFlight[] | undefined,
    outbound: FReservationFlight[] | undefined,
  ) => {
    return {
      name,
      index,
      logo,
      inbound,
      outbound
    };
  };

  const currentAirlinesFlight = [
    airlines(
      "ANA",
      0,
      require("../../../img/share/flight/logo_ana.png").default,
      getCurrentFlight(currentFlightEntities,"ANA", "inbound"),
      getCurrentFlight(currentFlightEntities,"ANA", "outbound"),
    ),
    airlines(
      "JAL",
      1,
      require("../../../img/share/flight/logo_jal.png").default,
      getCurrentFlight(currentFlightEntities,"JAL", "inbound"),
      getCurrentFlight(currentFlightEntities,"JAL", "outbound"),
    ),
  ];

  //-------------------------------------------------
  //　モーダルの処理
  //-------------------------------------------------
  //モーダル初期表示ページを設定
  const [step, setStep] = React.useState(1);
  const stepHandle = (step: number) => {
    setStep(step)
  }

  // air_sub_no
  const [currentSubNumber, setSubNumber] = React.useState('1');

  const [currentFlights, setCurrentFlights] = React.useState<FlightType>();
  
  // モーダルのステータスを追加する
  const [connectingFlightModal, setConnectingFlightModal] = React.useState(false);
  const [flightModal, setFlightModal] = React.useState(false);

  // send flight data
  const [entities, setEntities] = React.useState<FReservationFlight[]>();

  // 乗継便の詳細モーダル
  const handleConnectingDetailModal = (airline:string, travelType:string, _flights:FReservationFlight[] | undefined) => {
    if(_flights) {
      setCurrentFlights({
        ...currentFlights,
        page: "top",
        airline: airline,
        currentNumbers: _flights.map((f) => f.air_sub_no),
        action: travelType,
        flight: _flights
      })
      setEntities(_flights)
    }
    setConnectingFlightModal(!connectingFlightModal)
  }

  // モーダルのhandleを追加する
  const handleConnectingFlightModal = () => {
    setConnectingFlightModal(!connectingFlightModal)
  }

  // section onclick button event
  const handleButtonOnClick = (airline:string, travelType:string, _flights:FReservationFlight[] | undefined) => {
    if(_flights) {
      setCurrentFlights({
        ...currentFlights,
        page: "top",
        airline: airline,
        currentNumbers: _flights.map((f) => f.air_sub_no),
        action: travelType,
        flight: _flights
      })
      setFlightModal(!flightModal)
    }
  } 

  const modalCallback = () => {
    setFlightModal(!flightModal)
  }

  const handleFlightSelectCallback = (airline:string, action:string, currentFlights: FReservationFlight[], airport: string | null | undefined ) => {
    changeFlightCallback(airline, action, currentFlights, airport)
    setFlightModal(!flightModal)
  }

  //------------------------------------------------------------------

  // ANAが就航していない区間の場合はJALのタブを初期状態にする
  const [value, setValue] = React.useState(anaHasNoFlight ? 1 : 0);
  useEffect(() => {
    if (anaHasNoFlight) {
      setValue(0)
    }
    if (anaHasNoFlight && !jalHasNoFlight) {
      setValue(1)
    }
    
  }, [anaHasNoFlight, jalHasNoFlight])

  //フライトタブ
  const handleChangeTabFlight = (
    event: React.ChangeEvent<{}>,
    newValue: number
  ) => {
    setValue(newValue);

    // タブ切り替え時に初期データの航空会社も切り替える
    changeAirportCallback(newValue === 0 ? "ANA" : "JAL");
    // changeFlightCallback(newValue === 0 ? "ANA" : "JAL", "outbound", currentCheapestFlights, false, true);
  };

  interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
  }
  
  function TabPanel(props: TabPanelProps) {
    const { children, value, index, ...other } = props;
  
    return (
      <div
        role="tabpanel"
        hidden={value !== index}
        id={`simple-tabpanel-${index}`}
        aria-labelledby={`simple-tab-${index}`}
        {...other}
      >
        {value === index && (
          <Box sx={{ p: 2 }}>
            <Typography component="div">{children}</Typography>
          </Box>
        )}
      </div>
    );
  }
  
  function a11yProps(index: number) {
    return {
      id: `simple-tab-${index}`,
      "aria-controls": `simple-tabpanel-${index}`,
    };
  }

  /**
   * ★条件 A（全社とも便を出せなかった）は**見出し＋再読み込みだけ**を出す。
   *   2026-09-04 再レビュー F1。
   *   タブまで描くと「便を取得できませんでした」と「運航している便がございません」が
   *   同時に並び、取得失敗なのか該当便なしなのか読み取れない。
   */
  const showSectionError = !!flightError && !isLoading.all;
  /**
   * どこかの社を取得中か。 2026-09-04 再レビュー F3
   * ★1 社だけ再読み込みしている間は、**もう 1 社の便変更も止める**。
   *   変更中に再読み込みが完了すると、どちらの結果が残るのか利用者に分からない。
   */
  const anyFlightLoading = isLoading.all || isLoading.ANA || isLoading.JAL || !!selectDisabled;

  return (
    <>
      <section>
          <div className="wrap-content-900">
            { canSelect == false ?
               <>
                <h2 className="heading-2">{t("フライトの選択")}</h2>
                <div className="box-white large mt-24">
                  <p className="c-light_gray fw-b">
                    {t("基本プランの日程を設定後に選択いただけます。")}
                  </p>
                </div> 
              </>
              : canSelect ?
              <>
                <h2 className="heading-2">{t("フライトの選択")}</h2>
                {/*
                  * ★便を取得できなかった区画の案内と再読み込み（issue #2992 / 報告書 §8 No.4）。
                  *   これが無いと、落ちた区間が「該当便なし」として表示され、
                  *   利用者は条件を変えて検索し直す＝上流コールが再発生する。
                  */}
                {showSectionError ? (
                  <SectionRetry
                    retryAfterSec={flightError!.retryAfterSec}
                    isLoading={isLoading.all}
                    onRetry={() => onRetryFlight?.()}
                    message={t("便を取得できませんでした。") ?? undefined}
                  />
                ) : (
                  <>
                    <p className="fz-16 fw-b mb-8 mb-20-md">
                      {t("ご希望のフライトをお選びください")}
                    </p>
                    <ul className="list-note mb-24">
                      <li key={"fsection_desc1"} >{t("往路・復路で異なる航空会社の組み合わせはできません。全ての便を同一の航空会社で選択してください。")}</li>
                      <li key={"fsection_desc2"} >{t("初日または最終日に基本プランを利用する場合は、接続時間に余裕を持ってフライトをご指定ください")}</li>
                    </ul>
                  </>
                )}
              </>:<></>
            }
          </div>

        { canSelect && !showSectionError ?
            <div className="wrap-content-900-no_padding">
            {/* フライト選択後 */}
            <div className="mt-24">
              <div className="tab-flight">
                <Box className="tab-flight-content">
                  <Box>
                    <Tabs
                      value={value}
                      onChange={handleChangeTabFlight}
                      className="tab-flight-content-list"
                      variant="fullWidth"
                      TabIndicatorProps={{ style: { background: "none" } }}
                    >
                      <Tab
                        label={t("ANAから選択")}
                        className="tab-flight-content-list-item"
                        style={(anaHasNoFlight && !airlineErrors?.ANA) || isLoading.JAL ? { color: "lightgray" } : {}}
                        // ★取得に失敗した社は「便なし」でも押せるようにする（2026-09-04 T3・条件 B）。
                        //   押せないとタブ内の再読み込みに到達できない
                        disabled={(anaHasNoFlight && !airlineErrors?.ANA) || isLoading.ANA}
                        {...a11yProps(0)}
                      />
                      <Tab
                        label={<>
                          <span style={{display:'inline-block'}}>
                            {t("JALから選択")}
                            {isLoading.all && isLoading.JAL && (<CircularProgress className={loadingStyle.flightTabLoading} size={10} color="primary" />)}
                          </span>
                        </>}
                        className="tab-flight-content-list-item "
                        style={(jalHasNoFlight && !airlineErrors?.JAL) || isLoading.JAL ? { color: "lightgray" } : {}}
                        // ★取得に失敗した社は「便なし」でも押せるようにする（条件 B）
                        disabled={(jalHasNoFlight && !airlineErrors?.JAL) || isLoading.JAL}
                      />
                    </Tabs>
                  </Box>
                  <div className="tab-flight-inner">
                    { currentAirlinesFlight.map((airline, index) => (
                      <TabPanel key={"tabp"+airline.index} value={value} index={airline.index}>
                        {
                          (airline.name === "ANA" && anaHasNoFlight) || (airline.name === "JAL" && jalHasNoFlight) ?
                            <div className="tab-flight-content-detail">
                              <div className="tab-flight-content-detail-list">
                                {/*
                                  * ★「取得できなかった」と「運航していない」を分ける（2026-09-04 T3）。
                                  *   同じ見た目にすると、利用者は条件を変えて検索し直す＝上流コールが再発生する。
                                  *   再読み込みはこの社だけを引き直す（もう 1 社の便と選択は維持）。
                                  */}
                                {airlineErrors?.[airline.name] && !isLoading.all ? (
                                  // ★`tab-flight-content-detail-list` は PC で `display: flex`。
                                  //   幅を持たせないと flex アイテムが内容幅に縮んで左寄りに見えるため、
                                  //   `w-100per` で親いっぱいに広げてから中央寄せする
                                  <div className="w-100per">
                                    <SectionRetry
                                      retryAfterSec={airlineErrors[airline.name].retryAfterSec}
                                      isLoading={airline.name === "ANA" ? isLoading.ANA : isLoading.JAL}
                                      onRetry={() => onRetryFlightAirline?.(airline.name)}
                                      message={t("{{airline}}の便を取得できませんでした。", {airline: airline.name}) ?? undefined}
                                    />
                                  </div>
                                ) : (
                                  <p className="fz-16 fw-b pb-10 ta-c">{t("当該区間では、運航している便がございません。")}</p>
                                )}
                              </div>
                            </div>
                            :
                              <LoadingOverlay
                                  active={airline.name === "ANA"? isLoading.ANA : isLoading.JAL}
                                  spinner
                              >
                            <div className="tab-flight-content-detail">
                              <div className="tab-flight-content-detail-list">
                                { airline.inbound ?
                                <div className="tab-flight-content-detail-list-item">
                                  <div className="box-flight-select">
                                    <div className="box-flight-select-heading">
                                      <>
                                        <ul className="box-flight-select-heading-label">
                                          <li key={"inbound_" + airline.name + index} className="box-flight-select-heading-label-item outbound">
                                            {t("往路")}
                                          </li>
                                          { isConnection(airline.inbound[0]) ?
                                            <li key={"ic" + airline.name + index} className="box-flight-select-heading-label-item connecting">
                                              {t("乗継便")}
                                            </li>
                                          : "" }
                                        </ul>
                                      </>
                                      <p className="box-flight-select-heading-text">
                                        {getFlightClassLabel(airline.inbound[0].class_code) ?
                                          <span className="class-code">{t(getFlightClassLabel(airline.inbound[0].class_code))}</span>
                                        : ""}
                                        <span>{dayjs(airline.inbound[0].date, "YYYYMMDD").locale(i18n.language).format(t('YYYY年MM月DD日（ddd）') ?? 'YYYY年MM月DD日（ddd）')}</span>
                                      </p>
                                    </div>
      
                                    <div className="box-flight-select-detail">
                                      <div className="box-flight-select-detail-schedule">
                                        <div className="box-flight-select-detail-schedule-airline">
                                          <div className="mr-10">
                                            <p className="box-flight-select-detail-schedule-airline-logo">
                                              <img
                                                src={ getAirLineLogo(airline.name, airline.inbound[0].air_code, airline.inbound[0].air_sub_no) }
                                                alt="フライト：住路"
                                              />
                                            </p>
                                            <p className="box-flight-select-detail-schedule-airline-label">
                                              { getFlightNumberForDisplay(airline.name, airline.inbound[0].air_code, airline.inbound[0].air_sub_no, i18n.language) }
                                            </p>
                                          </div>
      
                                          <div>
                                            <ul className="box-flight-select-detail-schedule-airline-time">
                                              <li key={"time_dep"+index} className="box-flight-select-detail-schedule-airline-time-item">
                                                <p className="box-flight-select-detail-schedule-airline-time-item-heading">
                                                {/* { r.inbound?.connection ? getFlightTime(getArrivalFlight(r.name, "inbound")?.dep) : getFlightTime(r.inbound?.dep)} */}
                                                {getFlightTime(airline.inbound[0].dep)}
                                                </p>
                                                <p className="box-flight-select-detail-schedule-airline-time-item-label">
                                                  {t(`Airport.${getFlightCodeLabel(airline.inbound[0].depapo)}`)}
                                                </p>
                                              </li>
                                              <li key={"time_arv"+index} className="box-flight-select-detail-schedule-airline-time-item">
                                                <p className="box-flight-select-detail-schedule-airline-time-item-heading">
                                                  { isConnection(airline.inbound[0]) ? getFlightTime(getArrivalFlight(airline.inbound)?.arv) : getFlightTime(airline.inbound[0].arv)}
                                                </p>
                                                <p className="box-flight-select-detail-schedule-airline-time-item-label">
                                                  { isConnection(airline.inbound[0]) ? t(`Airport.${getFlightCodeLabel(getArrivalFlight(airline.inbound)?.arrapo)}`) : t(`Airport.${getFlightCodeLabel(airline.inbound[0].arrapo)}`) }
                                                </p>
                                              </li>
                                            </ul>
                                          </div>
                                        </div>
                                      </div>
      
                                      <div className="box-flight-select-detail-status">
                                        {/* inbound */}
                                        <button
                                          className="button-border-medium"
                                          onClick={() => handleButtonOnClick(airline.name, "inbound", airline.inbound)}
                                          disabled={anyFlightLoading}
                                        >
                                          {t("変更する")}
                                        </button>
                                      </div>
                                    </div>
                                    { isConnection(airline.inbound[0]) ?
                                    <p>
                                      <button
                                        onClick={() => handleConnectingDetailModal(airline.name, "inbound", airline.inbound)}
                                        className="button-clear-blue-underline fz-13 fw-b p-0"
                                      >
                                        {t("乗継便詳細を見る")}
                                      </button>
                                    </p>
                                    : '' }
                                  </div>
                                </div> : ''
                                }
      
                                { airline.outbound ?
                                <div className="tab-flight-content-detail-list-item">
                                  <div className="box-flight-select">
                                    <div className="box-flight-select-heading">
                                      <>
                                        <ul className="box-flight-select-heading-label">
                                          <li key={"outbound_" + airline.name + index} className="box-flight-select-heading-label-item outbound">
                                            {t("復路")}
                                          </li>
                                          { isConnection(airline.outbound[0]) ?
                                            <li key={"oc" + airline.name + index} className="box-flight-select-heading-label-item connecting">
                                              {t("乗継便")}
                                            </li>
                                          : "" }
                                        </ul>
                                      </>
                                      <p className="box-flight-select-heading-text">
                                        {getFlightClassLabel(airline.outbound[0].class_code) ?
                                          <span className="class-code">{t(getFlightClassLabel(airline.outbound[0].class_code))}</span>
                                        : ""}
                                        <span>{dayjs(airline.outbound[0].date, "YYYYMMDD").locale(i18n.language).format(t('YYYY年MM月DD日（ddd）')?? 'YYYY年MM月DD日（ddd）')}</span>
                                      </p>
                                    </div>
      
                                    <div className="box-flight-select-detail">
                                      <div className="box-flight-select-detail-schedule">
                                        <div className="box-flight-select-detail-schedule-airline">
                                          <div className="mr-10">
                                            <p className="box-flight-select-detail-schedule-airline-logo">
                                              <img
                                                src={ getAirLineLogo(airline.name, airline.outbound[0].air_code, airline.outbound[0].air_sub_no) }
                                                alt="フライト：復路"
                                              />
                                            </p>
                                            <p className="box-flight-select-detail-schedule-airline-label">
                                              { getFlightNumberForDisplay(airline.name, airline.outbound[0].air_code, airline.outbound[0].air_sub_no, i18n.language) }
                                            </p>
                                          </div>
      
                                          <div>
                                            <ul className="box-flight-select-detail-schedule-airline-time">
                                              <li key={"otime_dep"+index} className="box-flight-select-detail-schedule-airline-time-item">
                                                <p className="box-flight-select-detail-schedule-airline-time-item-heading">
                                                  {getFlightTime(airline.outbound[0].dep)}
                                                </p>
                                                <p className="box-flight-select-detail-schedule-airline-time-item-label">
                                                  {t(`Airport.${getFlightCodeLabel(airline.outbound[0].depapo)}`)}
                                                </p>
                                              </li>
                                              <li key={"otime_arv"+index} className="box-flight-select-detail-schedule-airline-time-item">
                                                <p className="box-flight-select-detail-schedule-airline-time-item-heading">
                                                { isConnection(airline.outbound[0]) ? getFlightTime(getArrivalFlight(airline.outbound)?.arv) : getFlightTime(airline.outbound[0].arv)}
                                                </p>
                                                <p className="box-flight-select-detail-schedule-airline-time-item-label">
                                                { isConnection(airline.outbound[0]) ? t(`Airport.${getFlightCodeLabel(getArrivalFlight(airline.outbound)?.arrapo)}`) : t(`Airport.${getFlightCodeLabel(airline.outbound[0].arrapo)}`) }
                                                </p>
                                              </li>
                                            </ul>
                                          </div>
                                        </div>
                                      </div>
      
                                      <div className="box-flight-select-detail-status">
                                        {/* outbound */}
                                        <button
                                          className="button-border-medium"
                                          onClick={ () => handleButtonOnClick(airline.name, "outbound", airline.outbound) }
                                          disabled={anyFlightLoading}
                                        >
                                          {t("変更する")}
                                        </button>
                                      </div>
                                    </div>
                                    { isConnection(airline.outbound[0]) ?
                                      <p>
                                        <button
                                          onClick={() => handleConnectingDetailModal(airline.name, "outbound", airline.outbound)}
                                          className="button-clear-blue-underline fz-13 fw-b p-0"
                                        >
                                          {t("乗継便詳細を見る")}
                                        </button>
                                      </p>
                                      : '' }
                                  </div>
                                </div> : ''
                                }   
                              </div>
                            </div>
                            </LoadingOverlay>
                        }

                    </TabPanel>
                    )) }
                  </div>
                </Box>
              </div>
            </div>
          </div>
          :<></>
        }
      </section>


      {/* 乗継便確認 */}
      <ConnectingFlightModal flights={entities} airline={currentFlights?.airline} isShow={connectingFlightModal} callback={handleConnectingFlightModal} />
      {/* モーダル：フライト選択 */}
      {<FlightModalApi planSelectFormData={planSelectFormData} profitRate={profitRate} pageRef="top" changeFlightCallback={handleFlightSelectCallback} search={search} currentFlights={currentFlights} changeAirportCallback={changeAirportCallback} isShow={flightModal} callback={modalCallback} />}
    </>
  );
};

export default SectionFlight;
